import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { useSelector } from "react-redux";
import {
  useAddHours,
  useDeleteHour,
  useGetJobDetails,
  useGetWorkerHours,
} from "../apiCalls/jobApiCalls";
import { useGetUserDetails } from "../apiCalls/userApiCalls";
import {
  KeyboardArrowRight,
  ExpandMore,
  ArrowBackIos,
} from "@mui/icons-material";
import moment from "moment";
import { formatted } from "../utils";
import TimeRangePicker from "../components/TimeRangePicker";

export default function AddHours() {
  const [workerHoursDetailsVisibility, setWorkerHoursDetailsVisibility] =
    useState({});

  const workerIdsInputElement = useRef();
  const lunchInputElement = useRef();

  const [clockIn, setClockIn] = useState(null);
  const [clockOut, setClockOut] = useState(null);
  const [totalHours, setTotalHours] = useState(0);

  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;
  const userId = currentUser.user._id;
  const { jobId } = useParams();

  const { isLoading: isWorkerHoursLoading, data: workerHours } =
    useGetWorkerHours(jobId, token);

  const { mutate: deleteHourMutate, isLoading: isDeleteHourLoading } =
    useDeleteHour();

  const { isLoading: isUserLoading, data: userDetails } = useGetUserDetails(
    userId,
    token
  );
  const {
    mutate: addHoursMutate,
    isLoading: isAddHoursLoading,
    isError: isAddHoursError,
    error: addHoursError,
  } = useAddHours();
  const { isLoading: isJobDetailsLoading, data: jobDetails } = useGetJobDetails(
    jobId,
    token
  );
  const handleSubmit = (event) => {
    event.preventDefault();

    const lunchInput = lunchInputElement.current?.value;

    let addedHours = totalHours;
    if (lunchInput === "30m") {
      addedHours -= 0.5;
    } else if (lunchInput === "1hr") {
      addedHours -= 1;
    }

    const data = {
      jobId: jobId,
      token: token,
      lunch: lunchInput,
      workerId: workerIdsInputElement.current?.value,
      hours: addedHours,
      date: clockIn,
      clockOut: clockOut,
    };

    addHoursMutate(data);
  };

  function handleWorkerHoursDetails(workerId) {
    setWorkerHoursDetailsVisibility((prevState) => ({
      ...prevState,
      [workerId]: !prevState[workerId],
    }));
  }

  const handleDelete = (id) => {
    const data = {
      hourId: id,
      token: token,
    };
    deleteHourMutate(data);
  };

  // Callback to receive time difference from TimeRangePicker and update input
  const handleTimeDifferenceChange = (data) => {
    setClockIn(data.clockIn);
    setClockOut(data.clockOut);
    setTotalHours(data.hoursDifference);
  };

  const fallbackImage = "/images/avatar.jpg";

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="mx-6 md:mx-32 lg:mx-44 xl:mx-64 mt-32 w-full lg:w-2/5">
          <div className="fixed lg:left-20 xl:left-32 mt-2 hidden lg:flex lg:items-center lg:justify-center items-center justify-center w-10 h-10 rounded-full bg-white shadow btn-shadow hover:shadow-s, hover:bg-stone-50 transition-colors">
            <button
              onClick={() => window.history.back()}
              className="w-full h-full rounded-full flex items-center justify-center"
            >
              <ArrowBackIos
                style={{ fontSize: 24, paddingLeft: 6, color: "gray" }}
              />
            </button>
          </div>
          <h1 className="text-2xl font-medium pb-4">Job Details</h1>
          {isJobDetailsLoading ? (
            <Loader />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between px-6 border border-gray-100 btn-shadow py-3 rounded-xl">
                <div className="">
                  <p className="text-xl">
                    <b>{jobDetails.data.data.title}</b>
                  </p>
                  <p className="">
                    Started on:{" "}
                    {moment(jobDetails.data.data.addedOn).format(
                      "MMM DD, YYYY"
                    )}
                  </p>
                  {jobDetails.data.data.typeTitle && (
                    <p className="">Type: {jobDetails.data.data.typeTitle}</p>
                  )}
                  <p className="">
                    {jobDetails.data.data.jobType &&
                    jobDetails.data.data.jobType !== "Percentage"
                      ? `Area/Items: ${jobDetails.data.data.amount} ${jobDetails.data.data.jobType}`
                      : `Amount: ${formatted(jobDetails.data.data.amount)}`}
                  </p>

                  <p className="">
                    Sales Person: {jobDetails.data.data.salesId?.name}
                  </p>
                </div>
                <div className="sm:text-right">
                  <p className="">
                    Initial Budget:{" "}
                    {formatted(
                      (jobDetails.data.data.commissionRate *
                        jobDetails.data.data.amount) /
                        100
                    )}
                  </p>

                  {jobDetails.data.data.totalAdditionalBudget && (
                    <p className="">
                      Additional Budget:{" "}
                      {formatted(
                        jobDetails.data.data.totalAdditionalBudget | 0
                      )}
                    </p>
                  )}

                  <p className="font-semibold">
                    Total Budget:{" "}
                    {formatted(
                      Number(
                        (jobDetails.data.data.commissionRate *
                          jobDetails.data.data.amount) /
                          100
                      ) + Number(jobDetails.data.data.totalAdditionalBudget | 0)
                    )}
                  </p>
                </div>
              </div>
            </>
          )}

          {isUserLoading ? (
            <Loader />
          ) : (
            <>
              <div className="space-y-2 py-4">
                <label className="text-lg font-medium text-gray-900" htmlFor="">
                  Add Workers Hours
                </label>
                <div className="flex gap-4 flex-col items-center">
                  <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                    <select
                      name="workerIds"
                      className="w-full outline-none"
                      ref={workerIdsInputElement}
                    >
                      <option value="">Select Worker</option>
                      <option value={userId}>Self</option>
                      {userDetails?.data.data.workers.map((worker) => (
                        <option key={worker._id} value={worker._id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <TimeRangePicker onTimeChange={handleTimeDifferenceChange} />

                  <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                    <select
                      name="txtLunch"
                      className="w-full outline-none"
                      ref={lunchInputElement}
                    >
                      <option value="">No Lunch</option>
                      <option value="30m">30 Minutes</option>
                      <option value="1hr">01 Hour</option>
                    </select>
                  </div>

                  <button
                    onClick={handleSubmit}
                    className="text-white w-1/3 text-center px-4 py-2 md:py-3 mt-2 text-base font-medium tracking-wide  transition-al bg-[#59c3c2] hover:bg-teal-500 border border-gray-100 rounded-xl"
                  >
                    {isAddHoursLoading ? "... Is Adding" : "Add"}
                  </button>
                  {isAddHoursError && (
                    <div className="text-sm font-medium text-red-600 pt-2">
                      <p>{addHoursError.response.data.error}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {isWorkerHoursLoading ? (
            <Loader />
          ) : (
            <div className="space-y-3 pb-8 pt-10">
              <h1 className="text-2xl font-bold pb-4">Job Hours</h1>

              {workerHours.data.hours.map((worker) => (
                <div key={worker._id}>
                  <div
                    onClick={() => handleWorkerHoursDetails(worker._id)}
                    className="card flex items-center gap-4 bg-white card-shadow rounded-lg p-4"
                  >
                    <div
                      className="w-16 h-16 border border-gray-200  rounded-full"
                      style={{
                        backgroundImage: `url("${worker.workerImg}"), url("${fallbackImage}")`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                      }}
                    ></div>
                    <div className="grow">
                      <h2 className="text-lg font-medium tracking-wide">
                        {worker.workerName}
                      </h2>
                    </div>
                    <div className="text-4xl">
                      {workerHoursDetailsVisibility[worker._id] ? (
                        <ExpandMore
                          style={{ fontSize: "44", color: "#d8d8d8" }}
                        />
                      ) : (
                        <KeyboardArrowRight
                          style={{ fontSize: "44", color: "#d8d8d8" }}
                        />
                      )}
                    </div>
                  </div>

                  <div
                    className={`card flex flex-col bg-white card-shadow rounded-lg p-4 overflow-x-auto ${
                      workerHoursDetailsVisibility[worker._id] ? "" : "hidden"
                    }`}
                  >
                    <table className="w-full min-w-max">
                      <thead>
                        <tr>
                          <th className="text-left">Date</th>
                          <th className="text-center">Added by</th>
                          <th className="text-center">Hours</th>
                          <th className="text-center">Lunch</th>
                          <th className="text-center"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {worker.hourlyDetails.map((hourlyDetail, index) => (
                          <tr key={index}>
                            <td className="border-t border-gray-200">
                              {moment
                                .utc(hourlyDetail.date)
                                .format("MMM DD, YYYY")}
                            </td>
                            <td className="text-center border-t border-gray-200">
                              {hourlyDetail.addedBy}
                            </td>
                            <td className="text-center border-t border-gray-200">
                              {hourlyDetail.hours}
                            </td>
                            <td className="text-center border-t border-gray-200">
                              {hourlyDetail.lunch}
                            </td>
                            <td className="text-right border-t border-gray-200">
                              <button
                                onClick={() => handleDelete(hourlyDetail.id)}
                                className="px-1.5 py-0.5 text-xs font-medium tracking-wide border border-red-300 text-red-300 hover:bg-red-100 rounded-lg"
                              >
                                {isDeleteHourLoading ? "X.." : "X"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
